import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import './CloudflareDomainManager.css';

/**
 * 🌐 Cloudflare Domain Manager
 * 
 * Integrierte Lösung für:
 * - Cloudflare KV Synchronisation
 * - Domain Status Testing
 * - Debugging Tools
 * - DNS Setup Anleitung
 */
const CloudflareDomainManager = ({ domain, userId, username, spaceId, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  const [activeTab, setActiveTab] = useState('sync'); // sync, test, debug
  const [emailProvider, setEmailProvider] = useState('hostinger'); // Email Provider State
  const [mappingStatus, setMappingStatus] = useState(null); // Auto-check mapping status

  const functions = getFunctions();

  // Cloud Functions
  const updateCloudflareMapping = httpsCallable(functions, 'updateCloudflareMapping');
  const testCloudflareWorker = httpsCallable(functions, 'testCloudflareWorker');
  const getUserCloudflareMapping = httpsCallable(functions, 'getUserCloudflareMapping');
  const fixDomainDNS = httpsCallable(functions, 'fixDomainDNS');
  const verifyAndFixAllMappings = httpsCallable(functions, 'verifyAndFixAllMappings');

  /**
   * ONE-CLICK SETUP: Macht alles automatisch
   */
  const handleOneClickSetup = async () => {
    if (!domain || !userId || !username) {
      alert('Missing required data: domain, userId, or username');
      return;
    }

    setLoading(true);
    setSyncStatus(null);

    try {
      console.log('🚀 Starting One-Click Domain Setup:', { domain, userId, username, spaceId });

      // Step 1: Auto-Setup mit Cloudflare Zone Creation
      const result = await updateCloudflareMapping({
        domain,
        userId,
        username,
        spaceId: spaceId || 'default',
        action: 'add',
        autoSetup: true,  // Erstellt Cloudflare Zone
        fixDNS: true,     // Erstellt DNS Records + Worker Routes
        emailProvider     // Email Provider für DNS Records
      });

      console.log('✅ One-Click Setup completed:', result.data);

      setSyncStatus({
        success: true,
        data: result.data,
        timestamp: new Date().toISOString(),
        oneClickSetup: true
      });

      // Auto-test nach Setup und reload mapping status
      setTimeout(() => {
        handleTestDomain();
        autoCheckMapping(); // 🔥 NEW: Reload mapping status indicator
      }, 5000);

    } catch (error) {
      console.error('❌ One-Click Setup failed:', error);
      
      // Verbesserte Fehlermeldungen für häufige Probleme
      let userFriendlyError = error.message;
      let errorHint = '';
      
      if (error.message.includes('Zone not found')) {
        userFriendlyError = 'Domain-Zone konnte nicht gefunden werden';
        errorHint = 'Die Domain wurde noch nicht zu Cloudflare hinzugefügt. Bitte versuchen Sie es erneut oder kontaktieren Sie den Support.';
      } else if (error.message.includes('Auto-setup failed')) {
        userFriendlyError = 'Automatisches Setup fehlgeschlagen';
        errorHint = 'Die Cloudflare Zone konnte nicht erstellt werden. Bitte überprüfen Sie Ihre API-Konfiguration oder kontaktieren Sie den Support.';
      } else if (error.message.includes('DNS fix failed')) {
        userFriendlyError = 'DNS-Konfiguration fehlgeschlagen';
        errorHint = 'Die DNS Records konnten nicht erstellt werden. Die Zone existiert möglicherweise nicht oder die API-Berechtigungen sind unzureichend.';
      } else if (error.message.includes('unauthenticated')) {
        userFriendlyError = 'Authentifizierung fehlgeschlagen';
        errorHint = 'Bitte melden Sie sich erneut an und versuchen Sie es noch einmal.';
      }
      
      setSyncStatus({
        success: false,
        error: userFriendlyError,
        errorHint: errorHint,
        originalError: error.message,
        timestamp: new Date().toISOString(),
        oneClickSetup: true
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Synchronisiert Domain mit Cloudflare KV
   */
  const handleSyncToCloudflare = async (autoSetup = false) => {
    if (!domain || !userId || !username) {
      alert('Missing required data: domain, userId, or username');
      return;
    }

    setLoading(true);
    setSyncStatus(null);

    try {
      console.log('🔄 Syncing to Cloudflare KV:', { domain, userId, username, spaceId, autoSetup });

      const result = await updateCloudflareMapping({
        domain,
        userId,
        username,
        spaceId: spaceId || 'default',
        action: 'add',
        autoSetup,
        emailProvider
      });

      console.log('✅ Cloudflare sync result:', result.data);

      setSyncStatus({
        success: true,
        data: result.data,
        timestamp: new Date().toISOString(),
        autoSetup
      });

      // Auto-test nach erfolgreichem Sync
      setTimeout(() => {
        handleTestDomain();
      }, autoSetup ? 10000 : 2000); // Länger warten bei Auto-Setup

    } catch (error) {
      console.error('❌ Cloudflare sync failed:', error);
      setSyncStatus({
        success: false,
        error: error.message,
        timestamp: new Date().toISOString()
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Testet Domain-Status und Cloudflare Worker
   */
  const handleTestDomain = async () => {
    setLoading(true);
    setTestResults(null);

    try {
      console.log('🧪 Testing domain:', domain);

      const result = await testCloudflareWorker({
        domain
      });

      console.log('✅ Test results:', result.data);

      setTestResults({
        success: true,
        data: result.data,
        timestamp: new Date().toISOString()
      });

    } catch (error) {
      console.error('❌ Domain test failed:', error);
      setTestResults({
        success: false,
        error: error.message,
        timestamp: new Date().toISOString()
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Repariert DNS Records für Domain (verwendet updateCloudflareMapping mit fixDNS flag)
   */
  const handleFixDNS = async () => {
    if (!domain || !userId || !username) {
      alert('Missing required data: domain, userId, or username');
      return;
    }

    setLoading(true);
    setSyncStatus(null);

    try {
      console.log('🔧 Fixing DNS records for:', domain);

      // Verwende updateCloudflareMapping mit fixDNS Parameter
      const result = await updateCloudflareMapping({
        domain,
        userId,
        username,
        spaceId: spaceId || 'default',
        action: 'add',
        fixDNS: true, // Spezieller Parameter für DNS Fix
        emailProvider
      });

      console.log('✅ DNS fix result:', result.data);

      setSyncStatus({
        success: true,
        data: result.data,
        timestamp: new Date().toISOString(),
        dnsFixed: true
      });

      // Auto-test nach DNS Fix
      setTimeout(() => {
        handleTestDomain();
      }, 5000); // 5 Sekunden warten für DNS Propagation

    } catch (error) {
      console.error('❌ DNS fix failed:', error);
      setSyncStatus({
        success: false,
        error: error.message,
        timestamp: new Date().toISOString(),
        dnsFixed: false
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Lädt Debug-Informationen
   */
  const loadDebugInfo = async () => {
    try {
      const mappings = await getUserCloudflareMapping({ userId });
      
      setDebugInfo({
        userMappings: mappings.data || [],
        domain,
        userId,
        username,
        spaceId,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('❌ Failed to load debug info:', error);
      setDebugInfo({
        error: error.message,
        timestamp: new Date().toISOString()
      });
    }
  };

  /**
   * Verifiziert und korrigiert ALLE Domain-Mappings
   */
  const handleVerifyAndFixAllMappings = async () => {
    if (!window.confirm('⚠️  Dies überprüft und korrigiert ALLE Domain-Mappings im System.\n\nMöchten Sie fortfahren?')) return;

    setLoading(true);
    setDebugInfo(null);

    try {
      console.log('🔍 Starting verification of all mappings...');

      const result = await verifyAndFixAllMappings({});
      
      console.log('✅ Verification complete:', result.data);

      setDebugInfo({
        verificationComplete: true,
        checked: result.data.checked,
        fixed: result.data.fixed,
        results: result.data.results,
        message: result.data.message,
        timestamp: new Date().toISOString()
      });

    } catch (error) {
      console.error('❌ Verification failed:', error);
      setDebugInfo({
        verificationComplete: true,
        error: error.message,
        timestamp: new Date().toISOString()
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * 🔍 AUTO-CHECK: Prüft beim Laden, ob das Mapping korrekt ist
   */
  const autoCheckMapping = async () => {
    try {
      console.log('🔍 Auto-checking domain mapping...', { domain, expectedUsername: username, expectedSpaceId: spaceId });
      
      // Hole User-Mappings
      const mappingsResult = await getUserCloudflareMapping({ userId });
      const currentMapping = mappingsResult.data?.find(m => m.domain === domain);

      if (!currentMapping) {
        setMappingStatus({
          isCorrect: false,
          status: 'not_found',
          message: 'Domain wurde noch nicht zum Cloudflare System hinzugefügt',
          action: 'setup'
        });
        return;
      }

      // Prüfe ob das Mapping korrekt ist
      const isCorrect = currentMapping.username === username && 
                       currentMapping.spaceId === spaceId &&
                       currentMapping.userId === userId;

      if (isCorrect) {
        setMappingStatus({
          isCorrect: true,
          status: 'correct',
          message: `Domain zeigt korrekt auf Space "${username}"`,
          currentMapping
        });
      } else {
        setMappingStatus({
          isCorrect: false,
          status: 'wrong_space',
          message: `⚠️ Domain zeigt auf falschen Space!`,
          expected: { username, spaceId, userId },
          current: currentMapping,
          action: 'purge'
        });
        
        console.warn('❌ FALSCHES MAPPING ERKANNT:', {
          expected: { username, spaceId },
          current: { username: currentMapping.username, spaceId: currentMapping.spaceId }
        });
      }

    } catch (error) {
      console.error('❌ Auto-check failed:', error);
      setMappingStatus({
        isCorrect: false,
        status: 'error',
        message: 'Fehler beim Prüfen des Mappings',
        error: error.message
      });
    }
  };

  // Load debug info and auto-check mapping on mount
  useEffect(() => {
    loadDebugInfo();
    autoCheckMapping();
  }, [userId, domain, username, spaceId]);

  /**
   * Entfernt Domain aus Cloudflare KV
   */
  const handleRemoveFromCloudflare = async () => {
    if (!window.confirm(`Remove ${domain} from Cloudflare proxy system?`)) return;

    setLoading(true);

    try {
      const result = await updateCloudflareMapping({
        domain,
        userId,
        username,
        spaceId,
        action: 'remove'
      });

      setSyncStatus({
        success: true,
        data: result.data,
        action: 'removed',
        timestamp: new Date().toISOString()
      });

    } catch (error) {
      console.error('❌ Remove failed:', error);
      setSyncStatus({
        success: false,
        error: error.message,
        timestamp: new Date().toISOString()
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Verifiziert das aktuelle KV Mapping und bietet Fix an
   */
  const handleVerifyAndFixMapping = async () => {
    setLoading(true);
    setSyncStatus(null);

    try {
      console.log('🔍 Verifying KV mapping for:', domain);
      console.log('📝 Expected:', { username, spaceId, userId });

      // Teste das aktuelle Mapping
      const testResult = await testCloudflareWorker({ domain });
      
      console.log('🧪 Test result:', testResult.data);

      // Prüfe ob KV existiert
      if (!testResult.data?.domain?.kvFound) {
        setSyncStatus({
          success: false,
          error: 'Domain nicht im KV Storage gefunden',
          errorHint: 'Die Domain wurde noch nicht zum Cloudflare Proxy System hinzugefügt. Bitte führen Sie zuerst das Setup durch.',
          needsSetup: true,
          timestamp: new Date().toISOString()
        });
        setLoading(false);
        return;
      }

      // Hole die User-Mappings um das aktuelle Mapping zu prüfen
      const mappingsResult = await getUserCloudflareMapping({ userId });
      const currentMapping = mappingsResult.data?.find(m => m.domain === domain);

      if (!currentMapping) {
        setSyncStatus({
          success: false,
          error: 'Mapping nicht gefunden',
          errorHint: 'Domain ist im KV Storage, aber nicht in Firebase gespeichert.',
          needsSync: true,
          timestamp: new Date().toISOString()
        });
        setLoading(false);
        return;
      }

      // Prüfe ob das Mapping korrekt ist
      const isCorrect = currentMapping.username === username && 
                       currentMapping.spaceId === spaceId &&
                       currentMapping.userId === userId;

      if (isCorrect) {
        setSyncStatus({
          success: true,
          verified: true,
          data: {
            message: 'KV Mapping ist korrekt!',
            currentMapping: currentMapping,
            expected: { username, spaceId, userId }
          },
          timestamp: new Date().toISOString()
        });
      } else {
        setSyncStatus({
          success: false,
          verified: false,
          error: 'KV Mapping ist inkorrekt',
          errorHint: `Das Mapping zeigt auf "${currentMapping.username}" statt auf "${username}"`,
          data: {
            current: currentMapping,
            expected: { username, spaceId, userId },
            needsFix: true
          },
          timestamp: new Date().toISOString()
        });
      }

    } catch (error) {
      console.error('❌ Verify failed:', error);
      setSyncStatus({
        success: false,
        error: error.message,
        errorHint: 'Fehler beim Überprüfen des Mappings',
        timestamp: new Date().toISOString()
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Purge KV Cache: Korrigiert ein falsches KV Mapping (Remove + Re-Setup)
   * Löscht das alte Mapping und erstellt es neu, um gecachte Daten zu entfernen
   */
  const handleFixMapping = async () => {
    if (!window.confirm(`🔄 KV Cache für ${domain} purgen?\n\n✅ Was wird gemacht:\n• Altes KV Mapping wird gelöscht (Cache wird geleert)\n• Neues KV Mapping wird mit aktuellen Firebase-Daten erstellt\n• Domain zeigt danach auf die richtige Seite\n\n⏳ WICHTIG: Cloudflare KV braucht bis zu 60 Sekunden zum Propagieren!\n⚡ Nach dem Purge wird ein Cache-Buster-Link angezeigt.\n\nFortfahren?`)) return;

    setLoading(true);
    setSyncStatus(null);

    try {
      console.log('🔧 Fixing KV mapping for:', domain);

      // Step 1: Remove altes Mapping
      console.log('🗑️ Step 1: Removing old mapping...');
      await updateCloudflareMapping({
        domain,
        userId,
        username,
        spaceId,
        action: 'remove'
      });

      // Kurz warten
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Step 2: Erstelle korrektes Mapping
      console.log('✅ Step 2: Creating correct mapping...');
      const result = await updateCloudflareMapping({
        domain,
        userId,
        username,
        spaceId: spaceId || 'default',
        action: 'add',
        autoSetup: false,
        fixDNS: false,
        emailProvider
      });

      console.log('✅ Mapping fixed:', result.data);

      setSyncStatus({
        success: true,
        fixed: true,
        data: result.data,
        message: 'KV Mapping erfolgreich korrigiert!',
        timestamp: new Date().toISOString()
      });

      // Auto-verify nach Fix und reload mapping status
      setTimeout(() => {
        handleVerifyAndFixMapping();
        autoCheckMapping(); // 🔥 NEW: Reload mapping status indicator
      }, 2000);

    } catch (error) {
      console.error('❌ Fix failed:', error);
      setSyncStatus({
        success: false,
        error: error.message,
        errorHint: 'Fehler beim Korrigieren des Mappings',
        timestamp: new Date().toISOString()
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cloudflare-domain-manager">
      <div className="cdm-header">
        <h3>🌐 Cloudflare Domain Manager</h3>
        <button className="cdm-close" onClick={onClose}>×</button>
      </div>

      <div className="cdm-domain-info">
        <div className="cdm-domain">{domain}</div>
        <div className="cdm-meta">
          <span>User: {username}</span>
          <span>Space: {spaceId || 'default'}</span>
        </div>
      </div>

      {/* 🎯 MAPPING STATUS INDICATOR */}
      {mappingStatus && (
        <div className={`cdm-mapping-status ${mappingStatus.isCorrect ? 'correct' : 'incorrect'}`}>
          {mappingStatus.status === 'correct' && (
            <div className="mapping-status-correct">
              <div className="status-icon">✅</div>
              <div className="status-content">
                <strong>Mapping ist korrekt!</strong>
                <p>Domain zeigt auf diesen Space: <code>{mappingStatus.currentMapping.username}</code></p>
              </div>
            </div>
          )}
          
          {mappingStatus.status === 'wrong_space' && (
            <div className="mapping-status-wrong">
              <div className="status-icon">⚠️</div>
              <div className="status-content">
                <strong>Domain zeigt auf falschen Space!</strong>
                <div className="mapping-comparison-compact">
                  <div className="mapping-line wrong">
                    <span className="label">Aktuell:</span>
                    <code>{mappingStatus.current.username}</code>
                  </div>
                  <div className="mapping-line correct">
                    <span className="label">Sollte sein:</span>
                    <code>{mappingStatus.expected.username}</code>
                  </div>
                </div>
                <p className="fix-hint">👉 Klicke auf <strong>"🔄 Purge KV Cache"</strong> unten, um das zu beheben!</p>
              </div>
            </div>
          )}
          
          {mappingStatus.status === 'not_found' && (
            <div className="mapping-status-not-found">
              <div className="status-icon">🔍</div>
              <div className="status-content">
                <strong>Domain noch nicht verbunden</strong>
                <p>Klicke auf <strong>"🎯 Setup Domain (One-Click)"</strong>, um die Domain zu verbinden.</p>
              </div>
            </div>
          )}
          
          {mappingStatus.status === 'error' && (
            <div className="mapping-status-error">
              <div className="status-icon">❌</div>
              <div className="status-content">
                <strong>Fehler beim Prüfen</strong>
                <p>{mappingStatus.message}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="cdm-tabs">
        <button 
          className={`cdm-tab ${activeTab === 'sync' ? 'active' : ''}`}
          onClick={() => setActiveTab('sync')}
        >
          🔄 Sync
        </button>
        <button 
          className={`cdm-tab ${activeTab === 'test' ? 'active' : ''}`}
          onClick={() => setActiveTab('test')}
        >
          🧪 Test
        </button>
        <button 
          className={`cdm-tab ${activeTab === 'debug' ? 'active' : ''}`}
          onClick={() => setActiveTab('debug')}
        >
          🔍 Debug
        </button>
      </div>

      {/* Sync Tab */}
      {activeTab === 'sync' && (
        <div className="cdm-content">
          <div className="cdm-section">
            <h4>🎯 Professional Domain Setup</h4>
            <p>Complete domain setup with automatic email configuration. No technical knowledge required!</p>
            
            {/* Email Provider Selection */}
            <div className="cdm-email-provider-section">
              <h5>📧 Email Provider</h5>
              <p>Select your email provider to automatically configure MX, SPF, DKIM and DMARC records:</p>
              <div className="cdm-email-provider-selector">
                <select 
                  value={emailProvider} 
                  onChange={(e) => setEmailProvider(e.target.value)}
                  className="cdm-email-provider-dropdown"
                >
                  <option value="hostinger">📧 Hostinger Email (Recommended)</option>
                  <option value="google">📧 Google Workspace</option>
                  <option value="microsoft">📧 Microsoft 365</option>
                  <option value="none">🚫 No Email Setup (Website Only)</option>
                </select>
                
                <div className="cdm-email-provider-info">
                  {emailProvider === 'hostinger' && (
                    <div className="cdm-provider-details">
                      <strong>✅ Hostinger Email</strong>
                      <p>Automatically configures: MX records, SPF, DKIM (3 keys), DMARC</p>
                      <small>Perfect for Hostinger hosting customers</small>
                    </div>
                  )}
                  {emailProvider === 'google' && (
                    <div className="cdm-provider-details">
                      <strong>✅ Google Workspace</strong>
                      <p>Configures: Gmail MX records, SPF for Google</p>
                      <small>For Google Workspace/Gmail business accounts</small>
                    </div>
                  )}
                  {emailProvider === 'microsoft' && (
                    <div className="cdm-provider-details">
                      <strong>✅ Microsoft 365</strong>
                      <p>Configures: Outlook MX records, SPF for Microsoft</p>
                      <small>For Microsoft 365/Outlook business accounts</small>
                    </div>
                  )}
                  {emailProvider === 'none' && (
                    <div className="cdm-provider-details">
                      <strong>⚠️ Website Only</strong>
                      <p>No email DNS records will be created</p>
                      <small>Choose this if you handle email separately</small>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="cdm-actions">
              <button 
                className="cdm-btn mega-setup"
                onClick={handleOneClickSetup}
                disabled={loading}
                title="Automatically creates everything: Cloudflare zone, DNS records, Worker routes, and SSL"
              >
                {loading ? '⏳ Setting up your domain...' : '🎯 Setup Domain (One-Click)'}
              </button>
              
              {/* Verify Mapping Button - Prominent */}
              <button 
                className="cdm-btn verify-mapping"
                onClick={handleVerifyAndFixMapping}
                disabled={loading}
                title="Überprüft ob das KV Mapping korrekt ist"
              >
                {loading ? '🔍 Checking...' : '🔍 Verify Mapping'}
              </button>
              
              {/* Purge KV Cache Button - Löst Cache-Probleme */}
              <button 
                className="cdm-btn force-refresh"
                onClick={handleFixMapping}
                disabled={loading}
                title="🔄 Purge KV Cache: Löscht den gecachten KV-Eintrag und erstellt ihn neu. Nutze dies, wenn die Domain die falsche Seite lädt (z.B. christinaspoerer statt hackthebrand-copy)."
              >
                {loading ? '🔄 Purging Cache...' : '🔄 Purge KV Cache'}
              </button>
              
              {/* Advanced Options - Collapsed by default */}
              <details className="cdm-advanced-options">
                <summary>🔧 Advanced Options</summary>
                <div className="cdm-advanced-buttons">
                  <button 
                    className="cdm-btn secondary"
                    onClick={handleFixDNS}
                    disabled={loading}
                    title="Fixes DNS records and Worker routes"
                  >
                    {loading ? '🔧 Fixing...' : '🔧 Fix DNS & Routes'}
                  </button>
                  
                  <button 
                    className="cdm-btn danger"
                    onClick={handleRemoveFromCloudflare}
                    disabled={loading}
                  >
                    🗑️ Remove Domain
                  </button>
                </div>
              </details>
            </div>

            {/* Sync Status */}
            {syncStatus && (
              <div className={`cdm-result ${syncStatus.success ? 'success' : 'error'}`}>
                <h5>
                  {syncStatus.success 
                    ? (syncStatus.oneClickSetup ? '🎉 Domain Setup Complete!' :
                       syncStatus.dnsFixed ? '🔧 DNS Records Fixed Successfully' : 
                       syncStatus.autoSetup ? '✨ Auto-Setup Successful' : '✅ Sync Successful')
                    : (syncStatus.oneClickSetup ? '❌ Domain Setup Failed' :
                       syncStatus.dnsFixed === false ? '❌ DNS Fix Failed' : '❌ Sync Failed')
                  }
                </h5>
                
                {syncStatus.success && syncStatus.data && (
                  <div className="cdm-sync-details">
                    {/* One-Click Setup Success Instructions */}
                    {syncStatus.oneClickSetup && (
                      <div className="cdm-one-click-success">
                        <div className="cdm-success-steps">
                          <h6>🎯 Your domain is ready! Here's what we did:</h6>
                          <ul>
                            <li>✅ Created Cloudflare zone for your domain</li>
                            <li>✅ Configured DNS records automatically</li>
                            <li>✅ Set up Worker routes for unlimited domains</li>
                            <li>✅ Activated SSL/TLS encryption</li>
                            <li>✅ Added domain to proxy system</li>
                            {emailProvider !== 'none' && (
                              <li>✅ Configured {emailProvider === 'hostinger' ? 'Hostinger' : emailProvider === 'google' ? 'Google Workspace' : 'Microsoft 365'} email DNS records</li>
                            )}
                          </ul>
                          
                          {emailProvider !== 'none' && (
                            <div className="cdm-email-success">
                              <h6>📧 Email Configuration Complete</h6>
                              <p>
                                <strong>Your emails will work automatically!</strong> We've configured all necessary DNS records:
                              </p>
                              <ul>
                                <li>MX records for mail delivery</li>
                                <li>SPF record for sender authentication</li>
                                {emailProvider === 'hostinger' && <li>DKIM records (3 keys) for email signing</li>}
                                <li>DMARC policy for email security</li>
                              </ul>
                              <div className="cdm-email-note">
                                <strong>💡 Important:</strong> All email DNS records are set to "DNS only" (grey cloud) to ensure proper email delivery.
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {syncStatus.data.instructions?.nameservers && (
                          <div className="cdm-final-step">
                            <h6>🔧 Final Step: Update Nameservers</h6>
                            <p>Go to your domain provider and change nameservers to:</p>
                            <div className="nameserver-list">
                              {syncStatus.data.instructions.nameservers.current.map((ns, index) => (
                                <div key={index} className="nameserver-item">
                                  <code>{ns}</code>
                                  <button 
                                    className="copy-btn"
                                    onClick={() => navigator.clipboard.writeText(ns)}
                                    title="Copy to clipboard"
                                  >
                                    📋 Copy
                                  </button>
                                </div>
                              ))}
                            </div>
                            <div className="cdm-timing">
                              <p>⏱️ <strong>Your domain will be live in 2-5 minutes!</strong></p>
                              <p>💡 Test your domain: <a href={`https://${domain}`} target="_blank" rel="noopener noreferrer">{domain}</a></p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {syncStatus.dnsFixed && syncStatus.data.records && (
                      <div className="cdm-detail">
                        <strong>DNS Records Created:</strong>
                        <ul>
                          {syncStatus.data.records.map((record, index) => (
                            <li key={index}>
                              {record.name} {record.type} → {record.target}
                            </li>
                          ))}
                        </ul>
                        <p style={{color: '#059669', fontWeight: 'bold', marginTop: '12px'}}>
                          🎉 Your domain should now work! DNS propagation may take a few minutes.
                        </p>
                      </div>
                    )}
                    
                    {syncStatus.data.proxyUrl && (
                      <div className="cdm-detail">
                        <strong>Proxy URL:</strong> {syncStatus.data.proxyUrl}
                      </div>
                    )}
                    
                    {/* Nameserver Instructions für Auto-Setup */}
                    {syncStatus.data.instructions?.nameservers && (
                      <div className="cdm-nameserver-instructions">
                        <h6>
                          {syncStatus.data.instructions.nameservers.status === 'active' ? 
                            '✅ Nameservers Active' : 
                            '🔧 Update Your Nameservers'
                          }
                        </h6>
                        <p className="nameserver-message">
                          {syncStatus.data.instructions.nameservers.message}
                        </p>
                        
                        {syncStatus.data.instructions.nameservers.status !== 'active' && (
                          <div className="nameserver-list">
                            <strong>Change your nameservers to:</strong>
                            {syncStatus.data.instructions.nameservers.current.map((ns, index) => (
                              <div key={index} className="nameserver-item">
                                <code>{ns}</code>
                                <button 
                                  className="copy-btn"
                                  onClick={() => navigator.clipboard.writeText(ns)}
                                  title="Copy to clipboard"
                                >
                                  📋
                                </button>
                              </div>
                            ))}
                            <div className="nameserver-help">
                              <small>
                                💡 Go to your domain provider and update the nameservers to the ones above. 
                                This usually takes 5-30 minutes to take effect.
                              </small>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* DNS Configuration für manuelles Setup */}
                    {syncStatus.data.instructions?.dns && !syncStatus.data.instructions?.nameservers && (
                      <div className="cdm-dns-instructions">
                        <h6>DNS Configuration:</h6>
                        {syncStatus.data.instructions.dns.map((record, index) => (
                          <div key={index} className="cdm-dns-record">
                            <span className="dns-type">{record.type}</span>
                            <span className="dns-name">{record.name}</span>
                            <span className="dns-value">{record.value}</span>
                            <span className="dns-ttl">TTL: {record.ttl}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {syncStatus.data.instructions?.manual && (
                      <div className="cdm-manual-instructions">
                        <h6>Manual KV Command:</h6>
                        <code>{syncStatus.data.instructions.manual.kvCommand}</code>
                      </div>
                    )}
                  </div>
                )}

                {!syncStatus.success && (
                  <div className="cdm-error-details">
                    <strong>❌ Error:</strong> {syncStatus.error}
                    {syncStatus.errorHint && (
                      <div className="cdm-error-hint">
                        <strong>💡 Hinweis:</strong> {syncStatus.errorHint}
                      </div>
                    )}
                    
                    {/* Show Fix Button if mapping is incorrect */}
                    {syncStatus.data?.needsFix && (
                      <div className="cdm-fix-section">
                        <h6>🔧 Mapping Korrektur verfügbar</h6>
                        <div className="cdm-mapping-comparison">
                          <div className="mapping-current">
                            <strong>❌ Aktuell:</strong>
                            <code>{JSON.stringify(syncStatus.data.current, null, 2)}</code>
                          </div>
                          <div className="mapping-expected">
                            <strong>✅ Erwartet:</strong>
                            <code>{JSON.stringify(syncStatus.data.expected, null, 2)}</code>
                          </div>
                        </div>
                        <button 
                          className="cdm-btn fix-mapping"
                          onClick={handleFixMapping}
                          disabled={loading}
                        >
                          {loading ? '🔧 Fixing...' : '🔧 Fix Mapping Now'}
                        </button>
                      </div>
                    )}
                    
                    {syncStatus.originalError && (
                      <details className="cdm-error-technical">
                        <summary>Technische Details</summary>
                        <code>{syncStatus.originalError}</code>
                      </details>
                    )}
                  </div>
                )}
                
                {/* Verify Success */}
                {syncStatus.success && syncStatus.verified && (
                  <div className="cdm-verify-success">
                    <h6>✅ KV Mapping ist korrekt!</h6>
                    <div className="cdm-verify-details">
                      <div className="verify-item">
                        <span className="verify-label">Username:</span>
                        <span className="verify-value">{syncStatus.data.currentMapping.username}</span>
                      </div>
                      <div className="verify-item">
                        <span className="verify-label">Space ID:</span>
                        <span className="verify-value">{syncStatus.data.currentMapping.spaceId}</span>
                      </div>
                      <div className="verify-item">
                        <span className="verify-label">User ID:</span>
                        <span className="verify-value">{syncStatus.data.currentMapping.userId}</span>
                      </div>
                      <div className="verify-item">
                        <span className="verify-label">Target URL:</span>
                        <span className="verify-value">{syncStatus.data.currentMapping.targetUrl}</span>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Fix Success */}
                {syncStatus.success && syncStatus.fixed && (
                  <div className="cdm-fix-success">
                    <h6>✅ {syncStatus.message}</h6>
                    <p>Das KV Mapping wurde erfolgreich korrigiert. Die Domain sollte jetzt auf den richtigen Space zeigen.</p>
                    <div className="cdm-timing">
                      <p>🔄 Automatische Verifizierung läuft...</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Test Tab */}
      {activeTab === 'test' && (
        <div className="cdm-content">
          <div className="cdm-section">
            <h4>Domain & Worker Testing</h4>
            <p>Testet die Cloudflare Worker Verbindung und Domain-Mapping.</p>
            
            <button 
              className="cdm-btn primary"
              onClick={handleTestDomain}
              disabled={loading}
            >
              {loading ? '🧪 Testing...' : '🧪 Test Domain'}
            </button>

            {/* Test Results */}
            {testResults && (
              <div className={`cdm-result ${testResults.success ? 'success' : 'error'}`}>
                <h5>{testResults.success ? '✅ Test Results' : '❌ Test Failed'}</h5>
                
                {testResults.success && testResults.data && (
                  <div className="cdm-test-details">
                    {/* Worker Status */}
                    <div className="cdm-test-section">
                      <h6>🔧 Worker Status</h6>
                      <div className="cdm-test-item">
                        <span>Status:</span>
                        <span className={testResults.data.worker?.success ? 'success' : 'error'}>
                          {testResults.data.worker?.status || 'Unknown'}
                        </span>
                      </div>
                      <div className="cdm-test-item">
                        <span>Running:</span>
                        <span className={testResults.data.worker?.isRunning ? 'success' : 'error'}>
                          {testResults.data.worker?.isRunning ? 'Yes' : 'No'}
                        </span>
                      </div>
                      <div className="cdm-test-response">
                        <strong>Response:</strong>
                        <code>{testResults.data.worker?.response}</code>
                      </div>
                    </div>

                    {/* Domain Test */}
                    {testResults.data.domain && (
                      <div className="cdm-test-section">
                        <h6>🌐 Domain Test</h6>
                        <div className="cdm-test-item">
                          <span>Status:</span>
                          <span className={testResults.data.domain.success ? 'success' : 'error'}>
                            {testResults.data.domain.status}
                          </span>
                        </div>
                        <div className="cdm-test-item">
                          <span>KV Found:</span>
                          <span className={testResults.data.domain.kvFound ? 'success' : 'error'}>
                            {testResults.data.domain.kvFound ? 'Yes' : 'No'}
                          </span>
                        </div>
                        <div className="cdm-test-item">
                          <span>Proxied:</span>
                          <span className={testResults.data.domain.isProxied ? 'success' : 'error'}>
                            {testResults.data.domain.isProxied ? 'Yes' : 'No'}
                          </span>
                        </div>
                        <div className="cdm-test-response">
                          <strong>Response:</strong>
                          <code>{testResults.data.domain.response}</code>
                        </div>
                      </div>
                    )}

                    {/* Debugging Info */}
                    {testResults.data.debugging && (
                      <div className="cdm-test-section">
                        <h6>🔍 System Info</h6>
                        <div className="cdm-test-item">
                          <span>KV Namespace:</span>
                          <span>{testResults.data.debugging.kvNamespace}</span>
                        </div>
                        <div className="cdm-test-item">
                          <span>Worker URL:</span>
                          <span>{testResults.data.debugging.workerSubdomain}</span>
                        </div>
                        <div className="cdm-test-item">
                          <span>API Token:</span>
                          <span className={testResults.data.debugging.hasApiToken ? 'success' : 'warning'}>
                            {testResults.data.debugging.hasApiToken ? 'Configured' : 'Missing'}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {!testResults.success && (
                  <div className="cdm-error-details">
                    <strong>Error:</strong> {testResults.error}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Debug Tab */}
      {activeTab === 'debug' && (
        <div className="cdm-content">
          <div className="cdm-section">
            <h4>Debug Information</h4>
            
            <div className="cdm-debug-actions">
              <button 
                className="cdm-btn secondary"
                onClick={loadDebugInfo}
                disabled={loading}
              >
                🔄 Refresh Debug Info
              </button>
              
              <button 
                className="cdm-btn verify-all-mappings"
                onClick={handleVerifyAndFixAllMappings}
                disabled={loading}
                title="Überprüft und korrigiert ALLE Domain-Mappings im System"
              >
                {loading ? '🔍 Checking All...' : '🔍 Verify & Fix ALL Mappings'}
              </button>
            </div>

            {debugInfo && debugInfo.verificationComplete && (
              <div className={`cdm-verification-results ${debugInfo.error ? 'error' : 'success'}`}>
                <h5>{debugInfo.error ? '❌ Verification Failed' : '✅ Verification Complete'}</h5>
                
                {!debugInfo.error && (
                  <div className="verification-summary">
                    <div className="summary-stat">
                      <span className="stat-label">Checked:</span>
                      <span className="stat-value">{debugInfo.checked} domains</span>
                    </div>
                    <div className="summary-stat">
                      <span className="stat-label">Fixed:</span>
                      <span className="stat-value fixed">{debugInfo.fixed} domains</span>
                    </div>
                    <p className="summary-message">{debugInfo.message}</p>
                    
                    {debugInfo.results && debugInfo.results.length > 0 && (
                      <div className="verification-details">
                        <h6>📋 Details:</h6>
                        <div className="results-list">
                          {debugInfo.results.map((result, index) => (
                            <div key={index} className={`result-item ${result.status}`}>
                              <div className="result-domain">{result.domain}</div>
                              <div className="result-status">
                                {result.status === 'correct' && <span className="status-badge correct">✅ Korrekt</span>}
                                {result.status === 'fixed' && (
                                  <span className="status-badge fixed">
                                    🔧 Fixed: {result.oldUsername} → {result.newUsername}
                                  </span>
                                )}
                                {result.status === 'skipped' && (
                                  <span className="status-badge skipped">⏭️ Skipped: {result.reason}</span>
                                )}
                                {result.status === 'failed' && (
                                  <span className="status-badge failed">❌ Failed: {result.error}</span>
                                )}
                                {result.status === 'error' && (
                                  <span className="status-badge error">⚠️ Error: {result.error}</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {debugInfo.error && (
                  <div className="verification-error">
                    <strong>Error:</strong> {debugInfo.error}
                  </div>
                )}
              </div>
            )}

            {debugInfo && !debugInfo.verificationComplete && (
              <div className="cdm-debug-info">
                <div className="cdm-debug-section">
                  <h6>📊 Current Configuration</h6>
                  <div className="cdm-debug-item">
                    <span>Domain:</span>
                    <span>{debugInfo.domain}</span>
                  </div>
                  <div className="cdm-debug-item">
                    <span>User ID:</span>
                    <span>{debugInfo.userId}</span>
                  </div>
                  <div className="cdm-debug-item">
                    <span>Username:</span>
                    <span>{debugInfo.username}</span>
                  </div>
                  <div className="cdm-debug-item">
                    <span>Space ID:</span>
                    <span>{debugInfo.spaceId || 'default'}</span>
                  </div>
                </div>

                {debugInfo.userMappings && debugInfo.userMappings.length > 0 && (
                  <div className="cdm-debug-section">
                    <h6>🌐 All User Domains in Cloudflare</h6>
                    {debugInfo.userMappings.map((mapping, index) => (
                      <div key={index} className="cdm-mapping-item">
                        <div className="mapping-domain">{mapping.domain}</div>
                        <div className="mapping-details">
                          <span>Username: {mapping.username}</span>
                          <span>Space: {mapping.spaceId}</span>
                          <span>Added: {new Date(mapping.timestamp).toLocaleDateString()}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {debugInfo.error && (
                  <div className="cdm-debug-error">
                    <strong>Debug Error:</strong> {debugInfo.error}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CloudflareDomainManager;
